import React, { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Quote, Star } from "lucide-react";
import { useSiteContent } from "@/lib/site-content";

function ReviewCard({ review, index }) {
  return (
    <div
      data-testid={`review-card-${index}`}
      className="flex-[0_0_100%] md:flex-[0_0_60%] lg:flex-[0_0_42%] pl-6"
    >
      <div className="h-full rounded-3xl bg-[#FAF8F5] border border-[#E8DED5] p-7 md:p-9 relative overflow-hidden">
        <Quote className="absolute -top-3 -right-3 h-28 w-28 text-[#E53935]/10" />
        <div className="flex items-center gap-1 text-[#E53935]">
          {Array.from({ length: review.rating || 5 }).map((_, i) => (
            <Star key={i} className="h-4 w-4 fill-[#E53935]" />
          ))}
        </div>
        <p className="mt-5 text-[17px] leading-relaxed text-[#1A1A1A]/90">
          “{review.text}”
        </p>
        <div className="mt-8 pt-6 border-t border-[#E8DED5] flex items-center gap-4">
          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#E53935] to-[#F5A623] text-white grid place-items-center font-display font-black">
            {(review.name || "?")[0]}
          </div>
          <div>
            <div className="font-display font-bold text-[15px]">
              {review.name}
            </div>
            <div className="text-xs text-[#5C534D]">
              {review.meta ? `${review.meta} · ` : ""}
              {review.when}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Reviews() {
  const { content } = useSiteContent();
  const reviews = content.reviews;
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    loop: true,
    containScroll: "trimSnaps",
  });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    emblaApi.reInit();
  }, [emblaApi, reviews.length]);

  if (!reviews.length) return null;

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="py-20 md:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <span className="chip">Guests say</span>
            <h2 className="mt-5 font-display font-black tracking-tighter text-4xl md:text-5xl lg:text-6xl max-w-2xl">
              Loved by <span className="text-[#E53935]">Manchester</span>,{" "}
              <span className="italic font-light">recommended</span> by locals.
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="reviews-prev"
              onClick={scrollPrev}
              aria-label="Previous review"
              className="h-12 w-12 rounded-full border-2 border-[#1A1A1A] grid place-items-center bg-white hover:bg-[#1A1A1A] hover:text-white transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              data-testid="reviews-next"
              onClick={scrollNext}
              aria-label="Next review"
              className="h-12 w-12 rounded-full border-2 border-[#1A1A1A] grid place-items-center bg-white hover:bg-[#1A1A1A] hover:text-white transition-colors"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="overflow-hidden -ml-6" ref={emblaRef} data-testid="reviews-carousel">
          <div className="flex">
            {reviews.map((r, i) => (
              <ReviewCard key={r.id || i} review={r} index={i} />
            ))}
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 mt-10">
          {reviews.map((_, i) => (
            <button
              key={i}
              onClick={() => emblaApi?.scrollTo(i)}
              aria-label={`Go to review ${i + 1}`}
              data-testid={`reviews-dot-${i}`}
              className={`h-2 rounded-full transition-all ${
                selectedIndex === i
                  ? "w-8 bg-[#E53935]"
                  : "w-2 bg-[#E8DED5] hover:bg-[#5C534D]"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
